"use client";

import { useEffect, useRef, useState } from "react";
import { useInPageTargetInView } from "@/components/marketing/use-in-page-target-in-view";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { AttributedLink } from "@/components/marketing/attributed-link";
import { trackEvent } from "@/lib/analytics";
import { isAnalyticsSuppressedPath } from "@/lib/analytics/suppressed-paths";
import {
  BUSINESS_WALKTHROUGH_CTA,
  BUSINESS_WALKTHROUGH_HREF,
  isBusinessIntentPath,
} from "@/lib/marketing/business-walkthrough-cta";

export interface StickyMobileCtaTarget {
  href: string;
  label: string;
}

/** The click event emitted by the sticky bar (PostHog + GA4). */
export const STICKY_MOBILE_CTA_CLICK_EVENT = "sticky_mobile_cta_clicked";

/**
 * Keep the global mobile CTA aligned with the commercial job of the page.
 * Premium visitors should never be pulled out of a sales-led Business or Site
 * Control journey and dropped into the free-project funnel.
 *
 * Business-intent routes (the /business page, the Business feature pages and
 * the Business-intent compare / guide pages) go to the Business walkthrough:
 * cold traffic sent straight to the Business checkout has never converted.
 */
/**
 * Compare pages whose visitor intent is Site Control: the registry pages whose
 * primary CTA is the Site Control walkthrough (`ctaVariant` "site-control*"),
 * plus every head-to-head `/compare/thesitebook-site-control-vs-*` page.
 * __tests__/marketing-sticky-mobile-cta-routing.test.ts keeps the slug list in
 * lockstep with the registry (the head-to-head pages are covered by the
 * prefix, not the list).
 */
export const SITE_CONTROL_INTENT_COMPARE_SLUGS = [
  "best-construction-site-access-software-uk",
  "best-construction-site-attendance-software-uk",
  "best-construction-site-sign-in-app-uk",
  "best-contractor-induction-software-uk",
  "best-digital-site-induction-software-uk",
  "best-rfi-software-construction-uk",
] as const;

const SITE_CONTROL_VS_PREFIX = "/compare/thesitebook-site-control-vs-";

function isSiteControlIntentComparePath(pathname: string): boolean {
  if (pathname.startsWith(SITE_CONTROL_VS_PREFIX)) return true;
  const normalised =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  return SITE_CONTROL_INTENT_COMPARE_SLUGS.some(
    (slug) => normalised === `/compare/${slug}`,
  );
}

export function stickyMobileCtaTarget(pathname: string): StickyMobileCtaTarget {
  if (pathname === "/business" || pathname.startsWith("/business/")) {
    return { href: "#book-walkthrough", label: BUSINESS_WALKTHROUGH_CTA };
  }

  if (isBusinessIntentPath(pathname)) {
    return { href: BUSINESS_WALKTHROUGH_HREF, label: BUSINESS_WALKTHROUGH_CTA };
  }

  if (pathname === "/site-control" || pathname.startsWith("/site-control/")) {
    return { href: "#book", label: "Book a Site Control walkthrough" };
  }

  if (
    pathname === "/principal-contractors" ||
    pathname.startsWith("/principal-contractors/") ||
    isSiteControlIntentComparePath(pathname)
  ) {
    return {
      href: "/site-control#book",
      label: "Book a Site Control walkthrough",
    };
  }

  // Every other marketing route (features, guides, free tools, compare
  // pages) goes to the /start-project funnel, the same destination as the
  // in-page acquisition CTAs — the sticky bar must not split the same
  // visitor's mobile journey back to the bare signup form.
  return { href: "/start-project", label: "Start your first project" };
}

/**
 * Report a sticky-bar click to PostHog and GA4. Both are consent-gated:
 * `trackEvent` refuses without accepted Analytics consent, and PostHog only
 * captures once the consent-gated SDK has loaded (its before_send is the
 * final barrier). Token-bearing paths never emit.
 */
export function trackStickyMobileCtaClick(
  pathname: string,
  target: StickyMobileCtaTarget,
): void {
  if (isAnalyticsSuppressedPath(pathname)) return;
  const payload = {
    route: pathname,
    label: target.label,
    destination: target.href,
  };
  if (posthog.__loaded) posthog.capture(STICKY_MOBILE_CTA_CLICK_EVENT, payload);
  trackEvent(STICKY_MOBILE_CTA_CLICK_EVENT, payload);
}

/**
 * Sticky bottom CTA bar — mobile only (hidden on md+).
 * Appears when the user scrolls past the hero sentinel (≈ top of page content).
 * z-40 so it sits above cookie banners but below modals (z-50+).
 */
export function StickyMobileCta() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const target = stickyMobileCtaTarget(pathname);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        // Show CTA once the sentinel (hero-height placeholder) leaves the viewport
        setVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // An in-page target (the walkthrough / booking form): hide the bar while
  // that form is on screen. Otherwise the bar sits over the form's own submit
  // button, and tapping it does nothing because you are already there.
  const targetInView = useInPageTargetInView(target.href);
  const showBar = visible && !targetInView;

  return (
    <>
      {/*
       * Sentinel: a zero-width, hero-height div at the top of the page.
       * When this scrolls out of view the CTA slides in.
       * pointer-events-none so it never interferes with layout.
       */}
      <div
        ref={sentinelRef}
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 h-[500px] w-0"
      />

      {/* Sticky mobile bar */}
      <div
        className={[
          "fixed bottom-0 left-0 right-0 z-40",
          "border-t border-border bg-surface px-4 pb-4 pt-3 shadow-lg",
          "transition-transform duration-300 ease-in-out",
          "md:hidden", // desktop: never shown
          showBar ? "translate-y-0" : "translate-y-full",
        ].join(" ")}
        // inert (not just aria-hidden) so the off-screen signup link is also
        // removed from the tab order — an aria-hidden element must not
        // contain focusable children
        inert={!showBar}
        data-sticky-mobile-cta-bar
      >
        <AttributedLink
          href={target.href}
          data-sticky-mobile-cta
          onClick={() => trackStickyMobileCtaClick(pathname, target)}
          className="flex min-h-[44px] w-full items-center justify-center rounded-lg bg-accent px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-accent-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
        >
          {target.label} &rarr;
        </AttributedLink>
      </div>
    </>
  );
}
